import { calculateSalaryBenefits, type SalaryBenefitsInput } from '../utils/salaryBenefits';

interface Props {
  value: SalaryBenefitsInput;
  onChange: (value: SalaryBenefitsInput) => void;
  taxRegime: 'old' | 'new';
  monthlySpecialAllowance: number;
  monthlyBasicDa: number;
}

export function SalaryBenefitsInputs({ value, onChange, taxRegime, monthlySpecialAllowance, monthlyBasicDa }: Props) {
  const update = <K extends keyof SalaryBenefitsInput>(key: K, next: SalaryBenefitsInput[K]) => {
    onChange({ ...value, [key]: next });
  };
  const applied = calculateSalaryBenefits(value, monthlySpecialAllowance, monthlyBasicDa, taxRegime);
  const money = (amount: number) => `₹${Math.round(amount).toLocaleString('en-IN')}`;
  const amountInput = (key: 'employeeNps' | 'employerNps' | 'mealCoupons', label: string) => (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input type="text" inputMode="numeric" value={value[key] || ''}
        onChange={(event) => update(key, Number(event.target.value.replace(/[^0-9]/g, '')))}
        placeholder="0" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
    </label>
  );

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">NPS &amp; Meal Coupons (optional)</h4>
      <p className="text-xs text-gray-500">
        Enter monthly amounts in either view. Employer NPS and meal coupons are allocated
        from Special Allowance within your entered CTC; they are benefits, not cash paid to your bank.
      </p>
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={value.enabledNps} onChange={(event) => update('enabledNps', event.target.checked)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        Include NPS contributions
      </label>
      {value.enabledNps && (
        <div className="ml-6 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          {amountInput('employeeNps', 'Employee NPS via payroll (₹/month)')}
          {amountInput('employerNps', 'Employer NPS within CTC (₹/month)')}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={value.governmentEmployer} onChange={(event) => update('governmentEmployer', event.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Central / State Government employer
          </label>
          <p className="text-xs text-gray-500">
            Employer Tier I NPS deduction: up to {applied.employerDeductionRate}% of Basic + eligible DA
            ({money(applied.employerDeduction)}/month applied). DA is assumed to qualify for retirement benefits.
            Employee NPS reduces cash take-home; the additional personal deduction is capped at ₹50,000/year
            in the old regime and is unavailable in the new regime. No employer matching is assumed.
          </p>
          {(applied.employer * 12 > 750000 || monthlyBasicDa * 12 > 5000000) && (
            <p className="text-xs text-amber-700">If combined employer PF, NPS and superannuation exceed ₹7.5 lakh/year,
              additional taxable perquisites and accretion may apply. These are not included in this estimate.</p>
          )}
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={value.enabledMeals} onChange={(event) => update('enabledMeals', event.target.checked)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
        Include meal coupons / meal card
      </label>
      {value.enabledMeals && (
        <div className="ml-6 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
          {amountInput('mealCoupons', 'Meal coupon value within CTC (₹/month)')}
          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={value.qualifyingMeals} onChange={(event) => update('qualifyingMeals', event.target.checked)}
              className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            Employer provides eligible vouchers usable only at eating joints for meals during working hours
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm text-gray-600">Working days / month
              <input type="number" min="0" max="31" value={value.workingDays}
                onChange={(event) => update('workingDays', Math.max(0, Math.min(31, Math.floor(Number(event.target.value) || 0))))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
            <label className="block text-sm text-gray-600">Eligible meals / day
              <input type="number" min="0" max="3" value={value.mealsPerDay}
                onChange={(event) => update('mealsPerDay', Math.max(0, Math.min(3, Math.floor(Number(event.target.value) || 0))))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Tax Year 2026–27: eligible vouchers are exempt up to ₹200 per meal in either regime.
            Exemption applied: {money(applied.mealExemption)}/month. Excess or ineligible vouchers are taxable.
            A cash meal allowance remains taxable salary and should stay in your cash components.
          </p>
        </div>
      )}
      {applied.allocationLimited && (
        <p role="status" className="text-sm text-amber-700">
          These benefits exceed Special Allowance ({money(monthlySpecialAllowance)}/month).
          Applied employer NPS: {money(applied.employer)}; meal coupons: {money(applied.meals)}.
          Increase Special Allowance in Customize Components or reduce the benefit amounts.
        </p>
      )}
    </div>
  );
}
